<template>
  <section id="documents">
    <h4>Your LLM custom data</h4>
    <div class="description">
      Enriching a Large Language Model (LLM) with additional data from uploaded documents or websites can be done here so the model can understand and utilize
      the new information effectively.
    </div>

    <div :class="`col-12 row`">
      <div class="col-4">
        <h4 class="mt">
          Document type
          <Tooltip title="Set the type of document you want to upload." />
        </h4>
        <Dropdown :items="Object.values(LlmDocumentType)" :value="selectedDocumentType" @input="setDocumentType($event)" />
      </div>
      <div v-if="newDocument.type == LlmDocumentType.URL" class="col-4">
        <h4 class="mt">
          Limit of pages to be processed
          <Tooltip
            title="Set the amount of (sub) pages to be processed. This is used to limit the amount of pages that are processed. This is useful to prevent the LLM from processing too many webpages."
          />
        </h4>
        <input type="number" v-model.number="newDocument.limit" min="1" placeholder="Enter the limit of pages to be processed" />"
      </div>
      <div v-if="newDocument.type === LlmDocumentType.FILE" class="col-4">
        <h4 class="mt">
          Add files to your LLM
          <Tooltip title="Add files from your local machine to the LLM. This is used to add your files to the LLM. You can add multiple files to the LLM." />
        </h4>
        <button class="btn btn--white dialogmanager border-blue margin-left-0 input-height" type="button" @click="triggerFileUpload">
          Upload new file
          <input hidden :accept="llmSupportedFiletypes()" id="file-input" type="file" ref="llmfile" @change="handleFileUpload()" />
        </button>
      </div>

      <div v-if="newDocument.type === LlmDocumentType.URL" class="col-12">
        <h4 class="mt">
          Add your URLs
          <Tooltip title="Add your URLs to the LLM. This is used to add your URLs to the LLM. You can add multiple URLs to the LLM." />
        </h4>
        <div class="form-group">
          <div class="form-group--inside" style="margin-right: 27px">
            <div v-if="newDocument.type === LlmDocumentType.URL">
              <Autocomplete
                v-model="newDocument.content"
                :rows="1"
                class="code padding-input"
                spellcheck="false"
                placeholder="for example: https://www.blits.ai"
              ></Autocomplete>
              <div type="button" class="copybutton" @click="addDocument()">Add</div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 mt-2">
        <h4 class="m-b-20">
          Added documents
          <Tooltip
            title="The documents you have added to the LLM will be displayed here. Once you press the train button, the documents will be added to the LLM."
          />
        </h4>
        <div class="row" v-for="(doc, index) in current.documents" :key="index" :id="'document-' + index">
          <div class="col-12 m-b-20">
            <div class="form-group">
              <span class="form-group--inside documents">
                {{ doc.displayName || doc.name || doc.link || 'unknown' }}
                <i class="icon-delete entity-delete" @click="deleteDocument(index)"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="empty m-b-20" v-if="current.documents.length === 0">Press the add button to add your document</div>
      </div>

      <div v-if="docError" class="invalid">{{ docError }}</div>
    </div>
  </section>
</template>

<script lang="ts">
import { Component, Vue, Prop, Mixins } from 'vue-property-decorator'
import { LLMObject, LLMDocument } from '../../../../../../common/types/llm-type'
import Autocomplete from '../../../../components/helpers/Autocomplete.vue'
import { LlmDocumentType } from '../../../../../../common/enums/bot/llm/llm-document-type.enum'
import { LLM_MAX_FILESIZE, LLM_SUPPORTED_FILETYPES } from '../../../../../../common/constants/llm-document.constants'
import popupService from '@/services/popup.service'
import LlmManagementMixin from '../../../../components/admin/bots/_mixins/llm-management.mixin'
import { OpenAIEmbeddingsModels } from '@common/enums/open-ai-models'
import { NomicEmbeddingsModelsEnabled } from '@common/enums/nomic/embeddings-model.enum'
import Dropdown from '@/components/helpers/Dropdown.vue'
import Tooltip from '@/components/helpers/Tooltip.vue'

@Component({ components: { Autocomplete, Dropdown, Tooltip } })
export default class LLMDocuments extends Mixins(LlmManagementMixin) {
  @Prop() current: LLMObject
  @Prop() variables: string[]
  @Prop() docError: string

  openAiEmbeddingsModels = OpenAIEmbeddingsModels
  nomicEmbeddingsModels = NomicEmbeddingsModelsEnabled

  selectedDocumentType = LlmDocumentType.FILE

  newDocument: LLMDocument = {
    name: '',
    type: LlmDocumentType.FILE,
    content: '',
    file: undefined,
    link: undefined,
    displayName: undefined,
    limit: 5
  }

  get LlmDocumentType() {
    return LlmDocumentType
  }

  setDocumentType(value: LlmDocumentType) {
    this.selectedDocumentType = value
    this.newDocument.type = value
  }

  llmSupportedFiletypes() {
    return LLM_SUPPORTED_FILETYPES
  }

  triggerFileUpload() {
    ;(this.$refs.llmfile as HTMLInputElement).click()
  }

  handleFileUpload() {
    this.$refs.llmfile = this.$refs.llmfile as Vue
    if (this.$refs.llmfile.files.length !== 0) {
      const formData = new FormData()
      formData.append(this.$refs.llmfile.files[0].name, this.$refs.llmfile.files[0])
      formData.forEach((file) => {
        if ((file as File).size > LLM_MAX_FILESIZE) {
          popupService.showError(`File is too big. Accepted size is ${LLM_MAX_FILESIZE / 1024 / 1024}MB`)
          ;(this.$refs.llmfile as HTMLInputElement).value = ''
        } else {
          this.newDocument.name = (this.$refs.llmfile as Vue).files[0].name
          this.newDocument.file = (this.$refs.llmfile as Vue).files[0]
        }
      })
    }
    this.addDocument()
  }

  async addDocument() {
    if (this.newDocument.type === LlmDocumentType.URL) {
      this.newDocument.file = undefined
      this.newDocument.link = this.newDocument.content
      this.newDocument.content = undefined
    } else if (this.newDocument.type === LlmDocumentType.FILE) {
      const fileExtension = this.newDocument.file.name.split('.').pop()?.toLowerCase()

      if (!LLM_SUPPORTED_FILETYPES.includes(`${fileExtension}`)) {
        popupService.showError(`File type ${fileExtension} is not supported`)
        return
      }

      this.newDocument.displayName = this.newDocument.name
      const result = await this.uploadFile(this.current.llmId, this.newDocument.file)

      if (result && result.llmId) {
        this.$emit('refresh')
      }

      this.newDocument.link = undefined
      this.newDocument.content = undefined
      this.newDocument.displayName = undefined
    }

    //@ts-ignore
    this.current.documents.push(this.newDocument)

    this.newDocument = {
      name: '',
      type: LlmDocumentType.FILE,
      content: '',
      file: undefined,
      link: undefined,
      displayName: undefined
    }
  }

  get getDocuments() {
    return this.current.documents
  }

  deleteDocument(index) {
    this.$emit('deleteDocument', index)
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/scss/variables';

// .btn--white {
//   max-height: 43px;
// }

.description {
  font-size: 14px;
  margin-bottom: 15px;
}

.empty {
  color: $grey;
  font-size: 12px;
}

.m-b-5 {
  margin-bottom: 5px;
}

.m-b-20 {
  margin-bottom: 20px;
}

.documents {
  background-color: $blue-background;
  padding: 10px 5px 10px 15px;
  border-radius: 12px;
  font-weight: 500;
  color: $default-dark;

  .entity-delete {
    margin-left: 5px;
  }
}
</style>

<!-- 
-webkit-box-shadow: 0 6px 30px rgba(51, 51, 51, 0.0784313725);
    -ms-box-shadow: 0 6px 30px rgba(51, 51, 51, 0.0784313725);
    box-shadow: 0 6px 30px rgba(51, 51, 51, 0.0784313725) -->
